//----------------------------------------------------------------------------------------------------------------------
// Gamepad Value Reader
//----------------------------------------------------------------------------------------------------------------------

import type { DeviceValueReader, GamepadValueReaderDefinition, InputState } from '../../../interfaces/input.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Types of gamepad input sources
 */
export type GamepadSourceType = 'button' | 'axis';

/**
 * Options for configuring a gamepad value reader
 */
export interface GamepadReaderOptions
{
    /**
     * Whether to use the analog value instead of boolean pressed state for buttons
     */
    useAnalogValue ?: boolean;

    /**
     * Deadzone value (0.0 - 1.0) for analog axes
     * Input values below this threshold will be treated as 0
     */
    deadzone ?: number;

    /**
     * Whether to invert the axis values
     */
    invert ?: boolean;
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * Reads values from gamepad input states
 */
export class GamepadValueReader implements DeviceValueReader
{
    /**
     * Whether this reader targets a button or analog axis.
     */
    readonly sourceType : GamepadSourceType;

    /**
     * Identifies the specific button or axis (e.g. "button-0", "axis-1")
     */
    readonly sourceKey : string;

    /**
     * When true, returns the pressure-sensitive float value instead of boolean pressed state for buttons.
     */
    private readonly useAnalogValue : boolean;

    /**
     * Axis values below this threshold are treated as 0 (default 10%)
     */
    private readonly deadzone : number;

    /**
     * Multiplies axis values by -1 when true
     */
    private readonly invert : boolean;

    /**
     * Creates a new GamepadValueReader
     *
     * @param sourceType
     * @param sourceKey - e.g. "button-0", "axis-1"
     * @param options
     */
    constructor(sourceType : GamepadSourceType, sourceKey : string, options : GamepadReaderOptions = {})
    {
        this.sourceType = sourceType;
        this.sourceKey = sourceKey;
        this.useAnalogValue = options.useAnalogValue || false;
        this.deadzone = options.deadzone || 0.1; // Default 10% deadzone
        this.invert = options.invert || false;
    }

    /**
     * Gets the value of the input source. Returns undefined for non-gamepad input states.
     *
     * @param state
     */
    public getValue(state : InputState) : boolean | number | undefined
    {
        // Check if the state is from the correct device type
        if(state.type !== 'gamepad')
        {
            return undefined;
        }

        switch (this.sourceType)
        {
            case 'button':
            {
                const buttonState = state.buttons[this.sourceKey];

                if(!buttonState)
                {
                    return undefined;
                }

                return this.useAnalogValue ? buttonState.value : buttonState.pressed;
            }

            case 'axis':
            {
                let value = state.axes[this.sourceKey];

                if(value === undefined)
                {
                    return undefined;
                }

                // Apply deadzone
                if(Math.abs(value) < this.deadzone)
                {
                    value = 0;
                }

                // Apply inversion if needed
                return this.invert ? -value : value;
            }

            default:
                return undefined;
        }
    }

    /**
     * Creates a GamepadValueReader from a colon-delimited string (e.g. "button:0", "axis:1")
     *
     * @param sourceTypeString
     * @param options
     */
    public static fromString(sourceTypeString : string, options : GamepadReaderOptions = {}) : GamepadValueReader
    {
        const [ sourceType, sourceKey ] = sourceTypeString.split(':');

        if(!sourceType || !sourceKey)
        {
            throw new Error(`Invalid gamepad source format: ${ sourceTypeString }`);
        }

        return new GamepadValueReader(
            sourceType as GamepadSourceType,
            sourceKey,
            options
        );
    }

    /**
     * Returns a JSON-serializable representation of this gamepad value reader.
     */
    public toJSON() : GamepadValueReaderDefinition
    {
        return {
            type: 'gamepad',
            sourceType: this.sourceType,
            sourceKey: this.sourceKey,
            options: {
                useAnalogValue: this.useAnalogValue,
                deadzone: this.deadzone,
                invert: this.invert,
            },
        };
    }
}

//----------------------------------------------------------------------------------------------------------------------
